import { useRef, useMemo, useState, useEffect } from "react";
import {
  Button,
  GridTable,
  SecondaryHeader,
  SecondaryHeaderButtonWrap,
  SecondaryHeaderInfo,
  SecondaryHeaderSearchField,
  Spinner,
} from "olive-react-myoneui";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PaginationTemplate from "../component/grid-table/PaginationTemplate";
import { getCourses } from "../../../api/mockApis";
import TextTemplate from "../component/grid-table/TextTemplate";
import { penEditIcon, removeIcon } from "../../../constants/icons";
import NameTemplate from "../component/grid-table/NameTemplate";
import { debounce } from "../../../utils/helper";
import ConfirmBox from "../component/ConfirmBox";
import AddOffcanvas from "./AddOffcanvas";
import EditOffcanvas from "./EditOffcanvas";
import {
  createSubTopic,
  getSubTopic,
  updateSubTopic,
  deleteSubTopic,
} from "../../../api/subTopicApis";
import { getTopic } from "../../../api/topicApis";

export default function SubTopics() {
  const [showConfirmBox, setShowConfirmBox] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [topic, setTopic] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState("");
  const [subTopic, setSubTopic] = useState("");
  const [subTopicList, setSubtopicList] = useState([]);
  const [selectedRelatedSubTop, setSelectedRelatedSubTop] = useState([]);
  const [subTopicId, setSubTopicId] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [data, setData] = useState([]);

  const [search, setSearch] = useState("");
  const [searchText, setSearchText] = useState("");
  const [filter, setFilter] = useState(null);

  const tableManager = useRef(null);
  const setTableManager = (tm) => (tableManager.current = tm);

  const debouncedSetSearchText = useMemo(
    () =>
      debounce((value) => {
        setPage(1);
        setSearchText(value);
      }, 500),
    []
  );

  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSetSearchText(value);
  };

  const onClearSearch = () => {
    setSearch("");
    setSearchText("");
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (!loading) setPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    if (!loading && newSize !== pageSize) {
      setPageSize(newSize);
      setPage(1);
    }
  };

  function formatDate(dateString) {
    const date = new Date(dateString);
    const options = {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    return date
      .toLocaleString("en-GB", options)
      .replace("AM", "am")
      .replace("PM", "pm");
  }

  const getallTopic = async () => {
    setLoading(true);
    try {
      const response = await getTopic();
      if (response) setTopic(response?.data?.data);
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || "Failed to fetch topics.");
    } finally {
      setLoading(false);
    }
  };

  const getallSubTopic = async (payload = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page,
        limit: pageSize,
        title: payload?.title ?? searchText,
      };

      const response = await getSubTopic(queryParams);

      if (response) {
        const formattedData = response?.data?.data.map((list) => ({
          ...list,
          topic_title: list?.topic?.title,
          createdAt: formatDate(list.createdAt),
        }));

        const formattedDataForDropdown = response?.data?.data?.map((list) => ({
          id: list._id,
          name: list?.title,
        }));

        setData(formattedData);
        setSubtopicList(formattedDataForDropdown);
        setTotalItems(response?.data?.pagination?.totalItems || 0);
      }
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || "Failed to fetch sub-topics.");
    } finally {
      setLoading(false);
    }
  };

  const handleeditSubTopic = (data) => {
    setShowEdit(true);
    if (data) {
      const selectedTopic = topic.find(
        (t) => t?._id?.toString() === data?.topic?._id
      );
      const formattedData = data?.relatedSubTopics?.map((list) => ({
        id: list?._id,
        name: list?.title,
        relatedSubTopics: data?.relatedSubTopics || [],
      }));
      setSelectedRelatedSubTop(formattedData);
      setSelectedTopic(selectedTopic);
      setSubTopicId(data?._id);
      setSubTopic(data?.title);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let selectedId = selectedRelatedSubTop.map((top) => top.id);
      let payload = {
        title: subTopic,
        topic: selectedTopic?._id,
        relatedSubTopics: selectedId || [],
      };
      const response = await createSubTopic(payload);
      if (response) {
        handleClose();
        setPage(1);
        await getallSubTopic();
        toast.success("Sub-topic created successfully.");
      }
    } catch (error) {
      console.error("err", error);
      toast.error(error?.response?.data?.message || "Failed to create sub-topic.");
    } finally {
      setLoading(false);
    }
  };

  const updateSubTopics = async (
    subTopicName,
    topicId,
    subTopicId,
    relatedSubTopicsId
  ) => {
    setLoading(true);
    let formatted =
      relatedSubTopicsId?.length > 0
        ? relatedSubTopicsId.map((data) => ({
            _id: data?.id,
            name: data?.name,
          }))
        : [];

    let payload = {
      title: subTopicName,
      topic: topicId,
      relatedSubTopics: formatted,
    };

    try {
      const response = await updateSubTopic(payload, subTopicId);
      if (response) {
        handleClose();
        await getallSubTopic();
        toast.success("Sub-topic updated successfully.");
      }
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || "Failed to update sub-topic.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubTopic = (data) => {
    setShowConfirmBox(true);
    if (data) setSubTopicId(data);
  };

  const deleteSubTopicById = async () => {
    setLoading(true);
    try {
      const response = await deleteSubTopic(subTopicId);
      if (response?.status === 200) {
        setShowConfirmBox(false);
        setSubTopicId(null);
        await getallSubTopic();
        toast.success("Sub-topic deleted successfully.");
      }
    } catch (error) {
      console.error("Error deleting topic:", error);
      toast.error(error?.response?.data?.message || "Failed to delete sub-topic.");
    } finally {
      setLoading(false);
    }
  };

  const handleHideConfirmBox = () => setShowConfirmBox(false);

  const handleClose = () => {
    setShowAdd(false);
    setShowEdit(false);
    setSelectedRelatedSubTop([]);
    setSelectedTopic(null);
    setSubTopicId(null);
    setSubTopic("");
  };

  useEffect(() => {
    getallSubTopic();
  }, [page, pageSize]);

  useEffect(() => {
    if (searchText !== "") {
      setPage(1);
      getallSubTopic({ title: searchText });
    }
  }, [searchText]);

  useEffect(() => {
    getallTopic();
  }, []);

  const columnData = [
    {
      id: 1,
      field: "title",
      visible: true,
      label: "Sub Topics",
      width: ".5fr",
      sortable: true,
      className: "grid-table-title",
      cellRenderer: TextTemplate,
    },
    {
      id: 2,
      field: "topic_title",
      visible: true,
      label: "Topics",
      width: "1fr",
      sortable: true,
      className: "grid-table-title",
      cellRenderer: TextTemplate,
    },
    {
      id: 3,
      field: "createdAt",
      visible: true,
      label: "Created At",
      width: ".5fr",
      sortable: false,
      className: "grid-header-text-center",
      cellRenderer: TextTemplate,
    },
    {
      id: 4,
      label: `Action`,
      width: ".5fr",
      sortable: false,
      resizable: false,
      className: "grid-header-text-center",
      cellRenderer: ({ data }) => (
        <div className="rgt-cell-inner rgt-text-truncate program-edit-hover justify-content-center">
          <Button
            variant="white"
            className="btn-round"
            onClick={() => handleeditSubTopic(data)}
            disabled={loading}
          >
            {penEditIcon} Edit
          </Button>
          <Button
            variant="white"
            className="btn-round"
            onClick={() => handleDeleteSubTopic(data?._id)}
            disabled={loading}
          >
            {removeIcon} Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="content-wrapper">
        <SecondaryHeader>
          <SecondaryHeaderInfo
            SecondaryHeaderTitle="Sub-topics"
            SecondaryHeaderText={`${totalItems} Sub-topics`}
            SecondaryText
          />
          <SecondaryHeaderSearchField
            placeholder="Search Sub-topics..."
            value={search}
            onChange={onSearchChange}
            onClear={onClearSearch}
            disabled={loading}
          />
          <SecondaryHeaderButtonWrap>
            <Button
              variant="secondary"
              onClick={() => setShowAdd(true)}
              disabled={loading}
            >
              Add
            </Button>
          </SecondaryHeaderButtonWrap>
        </SecondaryHeader>

        <div className="content-block p-2" style={{ position: "relative" }}>
          {loading && (
            <div style={{ textAlign: "center", margin: "30px 0" }}>
              <Spinner size="medium" />
            </div>
          )}

          {!loading && data.length === 0 && (
            <p style={{ textAlign: "center", marginTop: 20 }}>
              No Sub-topics found.
            </p>
          )}

          {!loading && data.length > 0 && (
            <GridTable
              key={data.length}
              searchText={searchText}
              highlightSearch={false}
              columns={columnData}
              onRowsRequest={() => ({
                rows: data,
                totalRows: totalItems,
              })}
              page={page}
              pageSize={pageSize}
              setPage={handlePageChange}
              setPageSize={handlePageSizeChange}
              totalPages={Math.ceil(totalItems / pageSize)}
              filter={filter}
              onLoad={setTableManager}
              components={{
                Footer: (props) => (
                  <PaginationTemplate
                    {...props}
                    page={page}
                    pageSize={pageSize}
                    totalPages={Math.ceil(totalItems / pageSize)}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    disabled={loading}
                  />
                ),
              }}
            />
          )}
        </div>
      </div>

      <ConfirmBox
        show={showConfirmBox}
        question="Are you sure you want to delete Sub-topics?"
        okText="Delete"
        cancelText="Cancel"
        onHide={handleHideConfirmBox}
        onSubmit={deleteSubTopicById}
      />

      <AddOffcanvas
        show={showAdd}
        topic={topic}
        subTopic={subTopic}
        setSubTopic={setSubTopic}
        handleSubmit={handleSubmit}
        selectedTopic={selectedTopic}
        setSelectedTopic={setSelectedTopic}
        onClose={handleClose}
        subTopicList={subTopicList}
        setSubtopicList={setSubtopicList}
        selectedRelatedSubTop={selectedRelatedSubTop}
        setSelectedRelatedSubTop={setSelectedRelatedSubTop}
        loading={loading}
      />

      <EditOffcanvas
        show={showEdit}
        topic={topic}
        subTopic={subTopic}
        subTopicId={subTopicId}
        setSubTopic={setSubTopic}
        handleSubmit={updateSubTopics}
        selectedTopic={selectedTopic}
        setSelectedTopic={setSelectedTopic}
        onClose={handleClose}
        subTopicList={subTopicList}
        setSubtopicList={setSubtopicList}
        selectedRelatedSubTop={selectedRelatedSubTop}
        setSelectedRelatedSubTop={setSelectedRelatedSubTop}
        loading={loading}
      />

      <ToastContainer position="top-right" autoClose={5000} />
    </>
  );
}
